import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { SERVICES_DATA, SERVICE_SLUGS } from "../lib/servicesData";

const SITE = "https://optimumhospitalitysolutions.com";

export default function ServicesPage() {
    useSEO({
        title: "Hospitality Consulting Services UK | Optimum Hospitality Solutions",
        description:
            "Operator-led hospitality consulting services across the UK: hotel operations, performance improvement, labour productivity, guest experience, pre-opening, audits, multi-site support and interim management.",
        path: "/services",
        keywords:
            "hotel operations consultant uk, hospitality consultant uk, hotel performance improvement, hospitality productivity consultant, hotel audit consultant, hotel operations support, hospitality business consultant, independent hotel consultant",
    });

    const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
            { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
        ],
    };

    const itemListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        itemListElement: SERVICE_SLUGS.map((slug, i) => ({
            "@type": "ListItem",
            position: i + 1,
            url: `${SITE}/services/${slug}`,
            name: SERVICES_DATA[slug].title,
        })),
    };

    return (
        <div data-testid="services-page">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(itemListSchema) }} />

            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                        <div className="lg:col-span-8">
                            <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                Our Expertise
                            </span>
                            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mt-6 leading-[1.02] tracking-tight text-brand-navy">
                                Hospitality consulting services.
                            </h1>
                            <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-2xl">
                                A focused consulting practice for UK hotel owners, hospitality operators and small groups, led personally by Sidney Adams. Eight specialist services covering operations, performance, people, guest experience and openings. Every engagement is scoped to the asset, the owner and the brief.
                            </p>
                            <div className="mt-9 flex flex-wrap gap-3">
                                <Link
                                    to="/booking"
                                    data-testid="services-hero-cta"
                                    className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover transition-colors"
                                >
                                    Book a Consultation <ArrowUpRight size={16} />
                                </Link>
                                <Link
                                    to="/contact"
                                    className="inline-flex items-center gap-2 px-7 py-3.5 border border-brand-navy text-brand-navy text-sm tracking-wide hover:bg-brand-navy hover:text-brand-stone transition-colors"
                                >
                                    Speak with us
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* 8 SERVICE CARDS - HUB */}
            <section>
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-16 lg:py-24">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-border">
                        {SERVICE_SLUGS.map((slug, i) => {
                            const s = SERVICES_DATA[slug];
                            return (
                                <Link
                                    key={slug}
                                    to={`/services/${slug}`}
                                    data-testid={`service-hub-${slug}`}
                                    className="bg-brand-stone p-8 lg:p-10 hover:bg-white transition-colors group flex flex-col"
                                >
                                    <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">
                                        {String(i + 1).padStart(2, "0")}
                                    </div>
                                    <h2 className="font-display text-xl lg:text-2xl mt-4 text-brand-navy leading-snug">
                                        {s.title}
                                    </h2>
                                    <p className="mt-4 text-sm text-brand-ink/75 leading-relaxed flex-1">
                                        {s.intro.length > 180 ? `${s.intro.slice(0, 180).trim()}…` : s.intro}
                                    </p>
                                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-brand-navy link-underline tracking-wide self-start">
                                        Learn more
                                        <ArrowUpRight size={14} />
                                    </span>
                                </Link>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PRICING / ENGAGEMENT TIERS */}
            <section className="border-t border-brand-border bg-white" data-testid="pricing-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
                        <div className="lg:col-span-7">
                            <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                Investment
                            </span>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-tight text-brand-navy">
                                Engagement<br/>structure.
                            </h2>
                            <p className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-2xl">
                                A clear framework for how we work together, from a complimentary discovery call through to ongoing advisory partnerships. Every engagement is scoped to the asset and the brief.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-border">
                        <div
                            data-testid="pricing-tier-discovery"
                            className="bg-brand-navy p-10 text-brand-stone relative md:col-span-2 lg:col-span-1 lg:row-span-2"
                        >
                            <div className="text-xs uppercase tracking-[0.25em] text-brand-champagne">Start here</div>
                            <h3 className="font-display text-2xl mt-4 text-brand-stone">Complimentary Discovery Call</h3>
                            <div className="mt-6 flex items-baseline gap-2">
                                <span className="font-display text-5xl text-brand-champagne">Free</span>
                                <span className="text-sm text-brand-stone/60">· 30 minutes</span>
                            </div>
                            <ul className="mt-8 space-y-3 text-sm text-brand-stone/85 leading-relaxed">
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>30-minute introductory conversation</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Initial discussion of goals and challenges</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>No obligation</li>
                            </ul>
                            <Link
                                to="/booking?type=discovery"
                                data-testid="pricing-cta-discovery"
                                className="mt-10 inline-flex items-center gap-2 px-6 py-3 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white transition-colors"
                            >
                                Book a free discovery call <ArrowUpRight size={16} />
                            </Link>
                        </div>

                        <div data-testid="pricing-tier-consultation" className="bg-brand-stone p-10">
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">02</div>
                            <h3 className="font-display text-xl mt-4 text-brand-navy">Operations Consultation</h3>
                            <div className="mt-5 flex items-baseline gap-2">
                                <span className="text-xs uppercase tracking-[0.15em] text-brand-ink/60">Investment</span>
                                <span className="font-display text-3xl text-brand-navy">From £250</span>
                            </div>
                            <ul className="mt-6 space-y-3 text-sm text-brand-ink/75 leading-relaxed">
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Up to 90 minutes</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Virtual or on-site</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Operational review and recommendations</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Follow-up summary report</li>
                            </ul>
                            <Link to="/booking?type=consultation" data-testid="pricing-cta-consultation" className="mt-8 inline-flex items-center gap-1.5 text-sm text-brand-navy link-underline tracking-wide">
                                Request consultation <ArrowUpRight size={14} />
                            </Link>
                        </div>

                        <div data-testid="pricing-tier-audit" className="bg-brand-stone p-10">
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">03</div>
                            <h3 className="font-display text-xl mt-4 text-brand-navy">Operational Audit</h3>
                            <div className="mt-5 flex items-baseline gap-2">
                                <span className="text-xs uppercase tracking-[0.15em] text-brand-ink/60">Investment</span>
                                <span className="font-display text-3xl text-brand-navy">From £1,000</span>
                            </div>
                            <ul className="mt-6 space-y-3 text-sm text-brand-ink/75 leading-relaxed">
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Comprehensive review of operations</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Guest journey assessment</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Team, standards and process evaluation</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Written recommendations report</li>
                            </ul>
                            <Link to="/booking?type=consultation" data-testid="pricing-cta-audit" className="mt-8 inline-flex items-center gap-1.5 text-sm text-brand-navy link-underline tracking-wide">
                                Enquire about an audit <ArrowUpRight size={14} />
                            </Link>
                        </div>

                        <div data-testid="pricing-tier-training" className="bg-brand-stone p-10">
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">04</div>
                            <h3 className="font-display text-xl mt-4 text-brand-navy">Training & Leadership Workshops</h3>
                            <div className="mt-5 flex items-baseline gap-2">
                                <span className="text-xs uppercase tracking-[0.15em] text-brand-ink/60">Investment</span>
                                <span className="font-display text-2xl text-brand-navy">Custom quotation</span>
                            </div>
                            <ul className="mt-6 space-y-3 text-sm text-brand-ink/75 leading-relaxed">
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Team development sessions</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Leadership coaching</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Service excellence training</li>
                            </ul>
                            <Link to="/booking?type=consultation" data-testid="pricing-cta-training" className="mt-8 inline-flex items-center gap-1.5 text-sm text-brand-navy link-underline tracking-wide">
                                Request a quotation <ArrowUpRight size={14} />
                            </Link>
                        </div>

                        <div data-testid="pricing-tier-advisory" className="bg-brand-stone p-10">
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">05</div>
                            <h3 className="font-display text-xl mt-4 text-brand-navy">Ongoing Advisory</h3>
                            <div className="mt-5 flex items-baseline gap-2">
                                <span className="text-xs uppercase tracking-[0.15em] text-brand-ink/60">Investment</span>
                                <span className="font-display text-2xl text-brand-navy">Custom quotation</span>
                            </div>
                            <ul className="mt-6 space-y-3 text-sm text-brand-ink/75 leading-relaxed">
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Monthly consultancy and strategic support</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Regular performance reviews</li>
                                <li className="flex gap-3"><span className="text-brand-champagne">—</span>Action planning and accountability</li>
                            </ul>
                            <Link to="/booking?type=consultation" data-testid="pricing-cta-advisory" className="mt-8 inline-flex items-center gap-1.5 text-sm text-brand-navy link-underline tracking-wide">
                                Enquire about advisory <ArrowUpRight size={14} />
                            </Link>
                        </div>
                    </div>

                    <p className="mt-12 text-sm text-brand-ink/70 leading-relaxed max-w-3xl italic">
                        All consulting engagements are tailored to the specific needs of each client. Following a discovery call, a bespoke proposal will be provided where appropriate.
                    </p>
                </div>
            </section>

            {/* Bottom CTA */}
            <section className="bg-brand-navy" data-testid="services-bottom-cta">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-brand-stone">
                            Ready to improve operational performance, profitability and guest experience?
                        </h2>
                        <p className="mt-5 text-brand-stone/75 max-w-2xl text-lg">
                            Schedule a confidential consultation with Optimum Hospitality Solutions.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right">
                        <Link
                            to="/booking"
                            data-testid="services-bottom-book"
                            className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white transition-colors"
                        >
                            Book a Consultation <ArrowUpRight size={16} />
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
